<template>
	<div class="upload-drag-files-wrapper">
		<!-- 拖拽上传区域 -->
		<div
			class="drag-upload-area"
			:class="{ 'is-disabled': isDisabled || fileList.length >= limit, 'is-dragover': isDragOver }"
			@dragover.prevent="handleDragOver"
			@dragleave.prevent="handleDragLeave"
			@drop.prevent="handleDrop"
		>
			<el-upload
				ref="uploadRef"
				class="drag-uploader"
				:action="action"
				:data="uploadData"
				:headers="uploadHeaders"
				:accept="accept"
				:limit="limit"
				:show-file-list="false"
				:before-upload="beforeUpload"
				:on-exceed="handleExceed"
				:on-success="handleSuccess"
				:on-error="handleError"
				:disabled="isDisabled || fileList.length >= limit"
				v-bind="$attrs"
				drag
			>
				<div v-if="fileList.length < limit" class="drag-content">
					<div class="drag-icon">
						<i class="el-icon-upload"></i>
					</div>
					<div class="drag-text">
						<p class="main-text">点击选择或拖拽上传附件</p>
						<p class="sub-text">支持 {{ acceptText }} 格式，单个文件不超过 {{ size }}MB，最多上传 {{ limit }} 个</p>
					</div>
					<el-button type="primary" size="small" class="select-btn" :disabled="isDisabled || fileList.length >= limit">
						{{ uploadButtonText }}
					</el-button>
				</div>
				<div v-else class="drag-content">
					<div class="drag-icon">
						<i class="el-icon-check"></i>
					</div>
					<div class="drag-text">
						<p class="main-text">已达到上传上限</p>
						<p class="sub-text">已上传 {{ fileList.length }} 个文件，如需更换请先删除已有文件</p>
					</div>
				</div>
			</el-upload>
		</div>

		<!-- 上传中遮罩 -->
		<div v-if="isUploading" class="upload-loading-mask">
			<i class="el-icon-loading"></i>
			<span>上传中...</span>
		</div>

		<!-- 提示文本 -->
		<div v-if="tip" class="files-uploader-tip">
			支持 {{ acceptText }} 格式，单个文件不超过 {{ size }}MB，最多上传 {{ limit }} 个
		</div>

		<!-- 文件列表 -->
		<transition-group name="file-list" tag="div" class="files-list">
			<div v-for="(item, index) in fileList" :key="item.fileId || item.uid || index" class="file-item">
				<div class="file-info">
					<!-- 文件类型图标 -->
					<div class="file-icon" :class="getFileIconClass(item.name)">
						<i :class="getFileIcon(item.name)" />
					</div>
					<!-- 文件名称 -->
					<div class="file-detail">
						<div class="file-name" :title="item.name">{{ item.name }}</div>
						<div v-if="item.size" class="file-size">{{ formatFileSize(item.size) }}</div>
					</div>
				</div>

				<!-- 操作按钮 -->
				<div class="file-actions">
					<el-button v-if="!isDisabled" type="text" size="mini" icon="el-icon-download" @click="handleDownload(item)">
						下载
					</el-button>
					<el-button
						v-if="!isDisabled"
						type="text"
						size="mini"
						icon="el-icon-delete"
						class="delete-btn"
						@click="handleDelete(item, index)"
					>
						删除
					</el-button>
				</div>
			</div>
		</transition-group>
	</div>
</template>

<script>
	import { MessageBox } from 'element-ui'
	import { getToken } from '@/utils/auth'

	/**
	 * @description 拖拽式附件上传组件（支持 Word、PDF 等文档）
	 * @example
	 *   <UploadDragFiles
	 *     v-model="fileList"
	 *     :action="uploadUrl"
	 *     :preview-url="previewUrl"
	 *     :delete-api="deleteApi"
	 *     accept=".doc,.docx,.pdf"
	 *     :limit="5"
	 *     :size="20"
	 *   />
	 */
	export default {
		name: 'UploadDragFiles',
		components: {},
		inheritAttrs: false,
		model: {
			prop: 'modelValue',
			event: 'update:modelValue'
		},
		props: {
			// 上传接口地址
			action: {
				type: String,
				required: true
			},
			// 额外上传参数
			data: {
				type: Object,
				default: () => ({})
			},
			// 文件列表（v-model）
			modelValue: {
				type: Array,
				default: () => []
			},
			// 最多上传数量
			limit: {
				type: Number,
				default: 5
			},
			// 单个文件大小限制（MB）
			size: {
				type: Number,
				default: 20
			},
			// 接受文件类型
			accept: {
				type: String,
				default: '.doc,.docx,.pdf'
			},
			// 是否显示提示
			tip: {
				type: Boolean,
				default: false
			},
			// 上传按钮文字
			uploadButtonText: {
				type: String,
				default: '选择附件'
			},
			// 是否禁用
			disabled: {
				type: Boolean,
				default: false
			},
			// 预览/下载基础 URL
			previewUrl: {
				type: String,
				default: ''
			},
			// 删除接口（传 Function）
			deleteApi: {
				type: Function,
				default: null
			},
			// 删除接口额外参数
			deleteParams: {
				type: Object,
				default: () => ({})
			}
		},
		data() {
			return {
				fileList: [],
				isUploading: false,
				isDragOver: false
			}
		},
		computed: {
			isDisabled() {
				if (this.disabled) return true
				let parent = this.$parent
				while (parent) {
					const parentProps = parent.$options.propsData || {}
					if (parentProps.disabled !== undefined) {
						return parentProps.disabled
					}
					parent = parent.$parent
				}
				return false
			},
			uploadHeaders() {
				return {
					InternetToken: getToken()
				}
			},
			uploadData() {
				return this.data
			},
			acceptText() {
				return this.accept
					.split(',')
					.map(ext => ext.replace('.', '').toUpperCase())
					.join('、')
			}
		},
		watch: {
			modelValue: {
				handler(newVal) {
					if (newVal && Array.isArray(newVal)) {
						this.fileList = [...newVal]
					}
				},
				immediate: true,
				deep: true
			}
		},
		methods: {
			/**
			 * 拖拽进入
			 */
			handleDragOver() {
				if (this.isDisabled || this.fileList.length >= this.limit) return
				this.isDragOver = true
			},
			/**
			 * 拖拽离开
			 */
			handleDragLeave() {
				this.isDragOver = false
			},
			/**
			 * 拖拽放下
			 */
			handleDrop() {
				this.isDragOver = false
			},
			/**
			 * 上传前的校验
			 * @param {File} file
			 * @returns {boolean}
			 */
			beforeUpload(file) {
				const fileName = file.name.toLowerCase()
				const fileExt = '.' + fileName.split('.').pop()
				const allowedExts = this.accept.split(',').map(ext => ext.trim().toLowerCase())
				const isTypeOk = allowedExts.includes(fileExt)
				const isSizeOk = file.size / 1024 / 1024 < this.size

				if (!isTypeOk) {
					this.$message.error(`仅支持 ${this.acceptText} 格式的文件`)
				}
				if (!isSizeOk) {
					this.$message.error(`单个文件大小不能超过 ${this.size}MB`)
				}
				if (isTypeOk && isSizeOk) {
					this.isUploading = true
				}
				return isTypeOk && isSizeOk
			},

			/**
			 * 超出上传数量的处理
			 */
			handleExceed() {
				this.$message.warning(`最多只能上传 ${this.limit} 个文件`)
			},

			/**
			 * 上传成功
			 * @param {Object} res
			 * @param {File} file
			 */
			handleSuccess(res, file) {
				this.isUploading = false
				if (res.code === 200) {
					const fileId = res.data
					const fileItem = {
						name: file.name,
						size: file.size,
						fileId: fileId,
						url: this.previewUrl ? this.previewUrl + fileId : '',
						uid: file.uid
					}
					this.fileList.push(fileItem)
					this.$emit('update:modelValue', this.fileList)
					this.$emit('uploadSuccess', fileItem, this.fileList)
					this.$message.success('上传成功')
				} else {
					this.$message.error(res.msg || '上传失败')
				}
			},

			/**
			 * 上传失败
			 */
			handleError() {
				this.isUploading = false
				this.$message.error('上传失败，请稍后重试')
				// 清除 el-upload 内部文件列表，避免上传失败文件占用 limit 计数
				this.$refs.uploadRef && this.$refs.uploadRef.clearFiles()
			},

			/**
			 * 下载/预览文件
			 * @param {Object} item
			 */
			handleDownload(item) {
				if (!item.url && !item.fileId) {
					this.$message.warning('文件地址不存在')
					return
				}
				const url = item.url || (this.previewUrl ? this.previewUrl + item.fileId : '')
				if (!url) {
					this.$message.warning('未配置预览地址')
					return
				}
				window.open(url, '_blank')
			},

			/**
			 * 删除文件
			 * @param {Object} item
			 * @param {Number} index
			 */
			handleDelete(item, index) {
				MessageBox.confirm(`确定要删除文件「${item.name}」吗？`, '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				})
					.then(() => {
						if (this.deleteApi) {
							return this.deleteApi({ ...this.deleteParams, fileRecordId: item.fileId })
								.then(() => {
									this.fileList.splice(index, 1)
								})
								.catch(error => {
									console.error('删除文件失败:', error)
									throw error
								})
						} else {
							this.fileList.splice(index, 1)
							return Promise.resolve()
						}
					})
					.then(() => {
						this.$emit('update:modelValue', this.fileList)
						this.$emit('deleteSuccess', item, this.fileList)
						this.$emit('change', this.fileList)
						this.$message.success('删除成功')
						// 清除 el-upload 内部文件列表，避免上传失败文件占用 limit 计数
						this.$refs.uploadRef && this.$refs.uploadRef.clearFiles()
					})
					.catch(error => {
						if (error !== 'cancel') {
							this.$message.error('删除失败')
						}
					})
			},

			/**
			 * 获取文件图标
			 * @param {String} fileName
			 * @returns {String}
			 */
			getFileIcon(fileName) {
				const ext = fileName
					.split('.')
					.pop()
					.toLowerCase()
				switch (ext) {
					case 'doc':
					case 'docx':
						return 'el-icon-document'
					case 'pdf':
						return 'el-icon-document-checked'
					case 'xls':
					case 'xlsx':
						return 'el-icon-document-copy'
					case 'txt':
						return 'el-icon-tickets'
					case 'zip':
					case 'rar':
					case '7z':
						return 'el-icon-folder'
					default:
						return 'el-icon-document'
				}
			},

			/**
			 * 获取文件图标样式类
			 * @param {String} fileName
			 * @returns {String}
			 */
			getFileIconClass(fileName) {
				const ext = fileName
					.split('.')
					.pop()
					.toLowerCase()
				switch (ext) {
					case 'doc':
					case 'docx':
						return 'word'
					case 'pdf':
						return 'pdf'
					case 'xls':
					case 'xlsx':
						return 'excel'
					default:
						return 'default'
				}
			},

			/**
			 * 格式化文件大小
			 * @param {Number} bytes
			 * @returns {String}
			 */
			formatFileSize(bytes) {
				if (!bytes) return ''
				if (bytes < 1024) return bytes + ' B'
				if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
				return (bytes / 1024 / 1024).toFixed(1) + ' MB'
			},

			/**
			 * 获取文件列表（供父组件 ref 调用）
			 * @returns {Array}
			 */
			getFileList() {
				return this.fileList
			},

			/**
			 * 清空文件列表
			 */
			clearFiles() {
				this.fileList = []
				this.$emit('update:modelValue', [])
				this.$emit('change', [])
			}
		}
	}
</script>

<style lang="scss" scoped>
	.upload-drag-files-wrapper {
		position: relative;
	}

	.drag-upload-area {
		position: relative;
		border: 2px dashed #d9d9d9;
		border-radius: 8px;
		background: #fafafa;
		cursor: pointer;
		transition: all 0.3s;
		padding: 40px 20px;
		text-align: center;
		overflow: hidden;

		&:hover {
			border-color: #409eff;
			background: #f0f9ff;
		}

		&.is-dragover {
			border-color: #409eff;
			background: #f0f9ff;
			box-shadow: 0 0 10px rgba(64, 158, 255, 0.2);
		}

		&.is-dragover:hover {
			border-color: #409eff;
			background: #f0f9ff;
		}

		&.is-disabled {
			cursor: not-allowed;
			opacity: 0.6;
			border-color: #d9d9d9;
			background: #fafafa;

			&:hover {
				border-color: #d9d9d9;
				background: #fafafa;
			}
		}
	}

	.drag-uploader {
		::v-deep .el-upload {
			display: block;
			width: 100%;
		}

		::v-deep .el-upload-dragger {
			width: 100%;
			background: transparent;
			border: none;
			border-radius: 0;
			padding: 0;
		}

		.select-btn {
			pointer-events: none;
		}
	}

	.drag-content {
		pointer-events: none;

		.drag-icon {
			font-size: 40px;
			color: #909399;
			margin-bottom: 12px;
		}

		.drag-text {
			margin-bottom: 16px;

			.main-text {
				font-size: 14px;
				color: #303133;
				margin: 0 0 8px;
			}

			.sub-text {
				font-size: 12px;
				color: #909399;
				margin: 0;
			}
		}
	}

	/* 上传中遮罩 */
	.upload-loading-mask {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(255, 255, 255, 0.8);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		z-index: 10;
		border-radius: 8px;

		i {
			font-size: 36px;
			color: #409eff;
			animation: rotating 1.5s linear infinite;
		}

		span {
			margin-top: 8px;
			color: #409eff;
			font-size: 14px;
		}
	}

	@keyframes rotating {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	.files-uploader-tip {
		margin-top: 8px;
		font-size: 12px;
		color: #909399;
		line-height: 1.5;
	}

	.files-list {
		margin-top: 12px;
	}

	.file-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 12px;
		margin-bottom: 8px;
		background: #f5f7fa;
		border: 1px solid #ebeef5;
		border-radius: 4px;
		transition: all 0.3s;

		&:hover {
			background: #ecf5ff;
			border-color: #c6e2ff;
		}
	}

	.file-info {
		display: flex;
		align-items: center;
		flex: 1;
		min-width: 0;
		margin-right: 12px;
	}

	.file-icon {
		width: 36px;
		height: 36px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
		margin-right: 10px;
		flex-shrink: 0;
		font-size: 18px;

		&.word {
			background: #e6f2ff;
			color: #2671cb;
		}

		&.pdf {
			background: #fef0f0;
			color: #f56c6c;
		}

		&.excel {
			background: #f0f9eb;
			color: #67c23a;
		}

		&.default {
			background: #f4f4f5;
			color: #909399;
		}
	}

	.file-detail {
		flex: 1;
		min-width: 0;
	}

	.file-name {
		font-size: 14px;
		color: #303133;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.file-size {
		font-size: 12px;
		color: #909399;
		margin-top: 2px;
	}

	.file-actions {
		flex-shrink: 0;
		white-space: nowrap;

		.delete-btn {
			color: #f56c6c;

			&:hover {
				color: #f78989;
			}
		}
	}

	/* 过渡动画 */
	.file-list-enter-active,
	.file-list-leave-active {
		transition: all 0.3s;
	}

	.file-list-enter,
	.file-list-leave-to {
		opacity: 0;
		transform: translateX(-20px);
	}
</style>
